import {CombinedTimelineDayComponentConfig, ProjectTimeTimelineDayContent} from "../timelineTypes";
import {ProjectTimeTimelineDayTitle} from "./projectTimeTimelineDayTitle";
import {ProjectTimeTimelineDayItems} from "./projectTimeTimelineDayItems";
import {ProjectTimeTimelineDayValidations} from "./projectTimeTimelineDayValidations";
import {ProjectTimeTimelineDayHoliday} from "./projectTimeTimelineDayHoliday";
import {CombinedRecordingStatus} from "@/shared/timr";

/**
 * Component to draw a single project time timeline day with all its content like title, validations, holiday and items.
 * Most of the content of this component is built using sub-components for each content type, which is appended to the day container, if available.
 * - The <code>ProjectTimeTimelineDayTitle</code> component is used to render the title section of the day including date and statistics.
 * - The <code>ProjectTimeTimelineDayValidations</code> component is used to render validation results for the day, if available.
 * - The <code>ProjectTimeTimelineDayHoliday</code> component is used to render holiday information for the day, if available.
 * - The <code>ProjectTimeTimelineDayItems</code> component is used to render project time items for the day, if available.
 *
 * This component is used for the "project time only" timeline mode and the "combined" timeline mode.
 * <code>TimelineDaysView} or {@link TimelineWeekView</code> create and append this component to draw each day in the timeline.
 * For an overview of the timeline architecture, have a look into the UML diagram "timeline_web_components.png"
 */
export class ProjectTimeTimelineDay extends HTMLElement {

	private _timelineDay: ProjectTimeTimelineDayContent;
	private _config: CombinedTimelineDayComponentConfig;
	private _combinedRecordingStatus: CombinedRecordingStatus;
	private _timelineDayContainer: HTMLElement;
	private _titleComponent: ProjectTimeTimelineDayTitle;
	private _itemsComponent: ProjectTimeTimelineDayItems;

	set timelineDay(day: ProjectTimeTimelineDayContent) {
		this._timelineDay = day;
	}

	get timelineDay(): ProjectTimeTimelineDayContent {
		return this._timelineDay;
	}

	set config(value: CombinedTimelineDayComponentConfig) {
		this._config = value;
	}

	get config(): CombinedTimelineDayComponentConfig {
		return this._config;
	}

	set combinedRecordingStatus(value: CombinedRecordingStatus) {
		this._combinedRecordingStatus = value;
		if (this._itemsComponent) {
			this._itemsComponent.combinedRecordingStatus = value;
		}
	}

	get combinedRecordingStatus(): CombinedRecordingStatus {
		return this._combinedRecordingStatus;
	}

	/**
	 * Main entry point to render the project time timeline day component.
	 *
	 * Base template for the project time timeline day component (title is always present, other content like validations, holiday and items are appended to the day container, if available)
	 * The generated html structure would look something like this:
	 * <project-time-timeline-day>
	 *   <project-time-timeline-day-title>...</project-time-timeline-day-title>
	 *   <div class="timeline-day">
	 *     <project-time-timeline-day-validations>...</project-time-timeline-day-validations>
	 *     <project-time-timeline-day-holiday>...</project-time-timeline-day-holiday>
	 *     <project-time-timeline-day-items>...</project-time-timeline-day-items>
	 *   </div>
	 * </project-time-timeline-day>
	 */
	public connectedCallback(): void {
		// create the day container
		const div = document.createElement("div");
		div.className = "timeline-day";
		this.appendChild(div);

		const dayContainer = this.getTimelineDayContainer();
		if (!dayContainer) {
			return;
		}

		this.drawTitle();
		this.drawValidations(dayContainer);
		this.drawHoliday(dayContainer);
		this.drawItems(dayContainer);
		this.drawEmptyInfo(dayContainer);
	}

	private drawTitle(): void {
		const titleComponent = document.createElement("project-time-timeline-day-title") as ProjectTimeTimelineDayTitle;
		if (!titleComponent) {
			return;
		}
		titleComponent.timelineDay = this.timelineDay;
		titleComponent.config = this.config;
		this.getTimelineDayContainer().before(titleComponent);
	}

	private drawItems(dayContainer: HTMLElement): void {
		if (this.timelineDay.items.length === 0) {
			return;
		}
		const itemsComponent = document.createElement("project-time-timeline-day-items") as ProjectTimeTimelineDayItems;
		itemsComponent.combinedRecordingStatus = this.combinedRecordingStatus;
		itemsComponent.timelineDay = this.timelineDay;
		itemsComponent.config = this.config;

		this._itemsComponent = itemsComponent;
		dayContainer.appendChild(itemsComponent);
	}

	private drawValidations(dayContainer: HTMLElement): void {
		if (this.timelineDay.validationResults.length === 0) {
			return;
		}
		const validationsComponent = document.createElement("project-time-timeline-day-validations") as ProjectTimeTimelineDayValidations;
		validationsComponent.timelineDay = this.timelineDay;

		dayContainer.appendChild(validationsComponent);
	}

	private drawHoliday(dayContainer: HTMLElement): void {
		if (!this.timelineDay.holiday) {
			return;
		}
		const holidayComponent = document.createElement("project-time-timeline-day-holiday") as ProjectTimeTimelineDayHoliday;
		holidayComponent.timelineDay = this.timelineDay;

		dayContainer.appendChild(holidayComponent);
	}

	private drawEmptyInfo(dayContainer: HTMLElement): void {
		if (this.timelineDay.items.length > 0 || this.timelineDay.validationResults.length > 0 || this.timelineDay.holiday) {
			return;
		}
		const emptyInfoEl = document.createElement("div");
		emptyInfoEl.dataset.emptyInfo = "";
		emptyInfoEl.classList.add("timeline-empty-info");
		emptyInfoEl.innerText = messages["timeline.info.empty"];
		dayContainer.appendChild(emptyInfoEl);
	}

	private getTimelineDayContainer(): HTMLElement {
		if (!this._timelineDayContainer) {
			this._timelineDayContainer = this.querySelector(".timeline-day");
		}
		return this._timelineDayContainer;
	}
}
